// Confirmations.js — Reports > Appointment Confirmations (SS-4138)
//
// One row per appointment in the range (the range applies to the APPOINTMENT date),
// including appointments Viva never sent anything for. Each row says how many
// confirmation requests went out, whether the patient confirmed and on which channel,
// and whether the appointment was later cancelled or deleted. Expanding a row shows
// every request with its delivery outcome — a reminder that never left us must never
// read as a send.
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Spinner,
  Select,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  TagLabel,
  useTheme,
  useColorMode,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faCircleCheck,
  faMagnifyingGlass,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useLocation } from "react-router-dom";
import { cqAPI } from "../../lib/Auth";
import { useAppContext } from "../../AppContext";

//White label: this app also ships as A-dec's Link AI, where the vendor's name must never
//appear. The host check is the one the rest of the app already uses (theme.js:3,
//MenuComponent.js:136, Logo/LogoComponent.js:11, LoginComponent/Signup.js:91), and a
//partner-owned practice's own brand is preferred when the api sends one, exactly as
//Support/SupportWidget.js:252 does. The hostname is a parameter so the rule can be tested
//without rewriting window.location.
const DEFAULT_BRAND = "Viva";
const ADEC_BRAND = "Link AI";

export const isAdecHostname = (hostname = window.location.hostname) =>
  hostname.includes("adec") || hostname.includes("a-dec");

//The locations a report covers, as entries of the account's GET /locations list (AppContext):
//the practice in ?bid=, else the ones in ?bids=, else every location the account has, plus
//the location of every row on screen (rows are only re-fetched on "Show Confirmations", so a
//narrowed ?bids= can still be showing another location's appointments). A practice missing
//from the list stays in as undefined: an unknown brand, not one less location.
export const locationsInScope = (locations, search, rows) => {
  //a superadmin's list is every practice, so look the bids up rather than scan per bid
  const byBid = new Map((locations || []).map((location) => [String(location.bid), location]));
  const params = new URLSearchParams(search || "");
  const bid = params.get("bid");
  const urlBids = bid ? [bid] : (params.get("bids") || "").split(",").filter(Boolean);
  const bids = new Set(urlBids.length ? urlBids : byBid.keys());
  (rows || []).forEach((row) => {
    if (row.bid) {
      bids.add(String(row.bid));
    }
  });
  return [...bids].map((scopeBid) => byBid.get(scopeBid));
};

//null means "name no one": the copy then reads without a name, never with the vendor's
export const brandName = (business, hostname = window.location.hostname, scopeLocations) => {
  const own = business?.brand;
  //a white-labelled host never shows the vendor's name, whatever the payload says: the
  //host is the deployment, a brand is only a preference
  if (isAdecHostname(hostname)) {
    return own && own !== DEFAULT_BRAND ? own : ADEC_BRAND;
  }
  if (own || !scopeLocations) {
    return own || DEFAULT_BRAND;
  }
  //no practice selected (the all-locations view, MenuComponent sets {}): the brand each
  //location carries in GET /locations — the partner's name, or 'Viva' for a practice of
  //ours — when they all agree. Different partners, a partner next to a Viva practice, or a
  //location whose brand is not known yet name no one, so a partner account never reads
  //the vendor's name.
  const brands = new Set(scopeLocations.map((location) => (location?.brand || "").trim()));
  return brands.size === 1 && !brands.has("") ? [...brands][0] : null;
};

//the api owns the channel labels (controllers/reports.js) so the table, the CSV and the
//summary string cannot drift apart. These are the values it sends on sends[].channel.
export const CHANNEL_FILTERS = [
  { value: "sms", label: "Text" },
  { value: "email", label: "Email" },
  { value: "voice", label: "Phone call" },
];

export const STATE_FILTERS = [
  { value: "active", label: "Not confirmed yet" },
  { value: "confirmed", label: "Confirmed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "deleted", label: "Deleted" },
];

const STATE_COLORS = {
  active: "gray",
  confirmed: "green",
  cancelled: "orange",
  deleted: "red",
};

export const rowMatchesFilters = (row, stateFilter, channelFilter) => {
  if (stateFilter !== "All" && row.appointment_state !== stateFilter) {
    return false;
  }
  if (channelFilter !== "All" && !(row.sends || []).some((send) => send.channel === channelFilter)) {
    return false;
  }
  return true;
};

//counts for the strip above the table: appointments per state, requests per channel and
//who took the confirmation. Computed from the rows on screen so the strip always agrees
//with the table.
export const totalsFor = (rows) => {
  const states = STATE_FILTERS.reduce((acc, state) => ({ ...acc, [state.value]: 0 }), {});
  const channels = CHANNEL_FILTERS.reduce((acc, channel) => ({ ...acc, [channel.value]: 0 }), {});
  let confirmedByViva = 0;
  let confirmedByOffice = 0;
  let notConfirmed = 0;
  let requests = 0;

  rows.forEach((row) => {
    if (states[row.appointment_state] !== undefined) {
      states[row.appointment_state] += 1;
    }
    (row.sends || []).forEach((send) => {
      requests += 1;
      if (channels[send.channel] !== undefined) {
        channels[send.channel] += 1;
      }
    });
    if (row.confirmed_by === "viva") {
      confirmedByViva += 1;
    } else if (row.confirmed_by === "office") {
      confirmedByOffice += 1;
    } else {
      notConfirmed += 1;
    }
  });

  return { states, channels, requests, confirmedByViva, confirmedByOffice, notConfirmed };
};

export const confirmationText = (row) => {
  if (row.confirmed_by === "viva") {
    const when = row.confirmed_at ? moment(row.confirmed_at).format("MMM D, YYYY h:mm a") : "time not recorded";
    return `${row.confirmed_channel || "Confirmed"} • ${when}`;
  }
  if (row.confirmed_by === "office") {
    return "Confirmed by office";
  }
  return "Not confirmed";
};

export const sendOutcomeText = (send) => {
  if (send.channel === "voice") {
    return send.status ? `Call ${send.status}` : "Call outcome unknown";
  }
  if (send.delivered === true) {
    return "Delivered";
  }
  if (send.delivered === false) {
    const reason = [send.failure_code, send.failure_message].filter((part) => part || part === 0).join(" – ");
    return reason ? `Not delivered (${reason})` : "Not delivered";
  }
  return "Sent, no delivery receipt yet";
};

//"how many requests were sent" plus what became of them. Deliberately not the channel
//list: a message that reached neither channel has no channel, so counting outcomes is the
//only summary that cannot mislead. The channels are in the expanded row and the totals.
export const requestsText = (row) => {
  if (!row.sends_count) {
    return "None sent";
  }
  const sends = row.sends || [];
  const delivered = sends.filter((send) => send.delivered === true).length;
  const failed = sends.filter((send) => send.delivered === false).length;
  return [
    `${row.sends_count} sent`,
    ...(delivered ? [`${delivered} delivered`] : []),
    ...(failed ? [`${failed} failed`] : []),
  ].join(" · ");
};

export const stateText = (row) => {
  const label = (STATE_FILTERS.find((state) => state.value === row.appointment_state) || {}).label
    || row.appointment_state;
  if (!row.state_changed_at) {
    return label;
  }
  const when = moment(row.state_changed_at).format("MMM D, YYYY h:mm a");
  return `${label} ${row.state_changed_at_is_approximate ? "as of" : "on"} ${when}`;
};

function ConfirmationsReport({ selectedBusiness }) {
  const { locations } = useAppContext() || {};
  const { search } = useLocation();
  const [rows, setRows] = useState([]);
  //never the vendor's name on a white-labelled build or to a partner account
  const brand = useMemo(
    () => brandName(selectedBusiness, undefined, locationsInScope(locations, search, rows)),
    [selectedBusiness, locations, search, rows]
  );
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState("Today");
  const [dateRange, setDateRange] = useState([
    moment().startOf("day").toDate(),
    moment().endOf("day").toDate(),
  ]);
  const [stateFilter, setStateFilter] = useState("All");
  const [channelFilter, setChannelFilter] = useState("All");
  const [expandedRows, setExpandedRows] = useState({});

  const [startDate, endDate] = dateRange;
  const theme = useTheme();
  const { colorMode } = useColorMode();

  const fetchConfirmations = async (params = {}) => {
    const { startDate: paramStart, endDate: paramEnd } = params;
    setIsLoading(true);
    try {
      const formattedStartDate = paramStart ? moment(paramStart).format("L") : moment(dateRange[0]).format("L");
      const formattedEndDate = paramEnd ? moment(paramEnd).format("L") : moment(dateRange[1]).format("L");
      const response = await cqAPI.get(
        `/reports/confirmations?rangeBeg=${formattedStartDate}&rangeEnd=${formattedEndDate}`
      );
      setRows(response?.type === "ok" ? (response.data || []) : []);
    } catch (error) {
      console.error("Error fetching appointment confirmations:", error);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Init from hash on mount (shareable)
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const sd = hashParams.get("startDate")
      ? moment(hashParams.get("startDate")).toDate()
      : moment().startOf("day").toDate();
    const ed = hashParams.get("endDate")
      ? moment(hashParams.get("endDate")).toDate()
      : moment().endOf("day").toDate();
    const sr = hashParams.get("selectedRange") || "Today";

    setDateRange([sd, ed]);
    setSelectedRange(sr);
    fetchConfirmations({ startDate: sd, endDate: ed });
  }, []);

  // Keep URL hash in sync
  useEffect(() => {
    const q = new URLSearchParams({
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
      selectedRange,
    }).toString();
    window.location.hash = q;
  }, [startDate, endDate, selectedRange]);

  const handleDateRangeSelection = (value) => {
    setSelectedRange(value);
    let s, e;
    switch (value) {
      case "Today":
        s = moment().startOf("day");
        e = moment().endOf("day");
        break;
      case "Yesterday":
        s = moment().subtract(1, "days").startOf("day");
        e = moment().subtract(1, "days").endOf("day");
        break;
      case "Last 7 days":
        s = moment().subtract(6, "days").startOf("day");
        e = moment().endOf("day");
        break;
      case "Last 4 weeks":
        s = moment().subtract(4, "weeks").startOf("day");
        e = moment().endOf("day");
        break;
      case "Month to Date":
        s = moment().startOf("month");
        e = moment().endOf("day");
        break;
      case "Quarter to date":
        s = moment().startOf("quarter");
        e = moment().endOf("day");
        break;
      case "Year to date":
        s = moment().startOf("year");
        e = moment().endOf("day");
        break;
      case "Custom":
      default:
        s = null;
        e = null;
    }
    setDateRange([s ? s.toDate() : null, e ? e.toDate() : null]);
  };

  const renderCustomHeader = ({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
    <Flex direction="column" width="100%" paddingX="20px">
      <Flex justifyContent="center" alignItems="center" mt={4}>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500" mr={2}>
          {startDate ? startDate.toLocaleDateString() : "Start Date"}
        </Box>
        <Text mx={2}>→</Text>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500">
          {endDate ? endDate.toLocaleDateString() : "End Date"}
        </Box>
      </Flex>
      <Flex justifyContent="space-between" alignItems="center" pt={2}>
        <Box as="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </Box>
        <Box>
          {date.toLocaleString("default", { month: "long", year: "numeric" })}
        </Box>
        <Box as="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
          <FontAwesomeIcon icon={faChevronRight} />
        </Box>
      </Flex>
    </Flex>
  );

  //the api sorts each location's rows by appointment date, but a multi-location report is
  //the locations' arrays concatenated (helpers/reportScope mergeData), so the table sorts
  //again to keep one chronological list
  const visibleRows = useMemo(
    () => rows
      .filter((row) => rowMatchesFilters(row, stateFilter, channelFilter))
      .sort((a, b) => new Date(a.starttime) - new Date(b.starttime)),
    [rows, stateFilter, channelFilter]
  );
  const totals = useMemo(() => totalsFor(visibleRows), [visibleRows]);
  const showLocationColumn = visibleRows.some((row) => row.location_name);

  const appointmentWidth = showLocationColumn ? "16%" : "18%";
  const patientWidth = showLocationColumn ? "15%" : "17%";
  const providerWidth = showLocationColumn ? "12%" : "15%";
  const requestsWidth = showLocationColumn ? "17%" : "20%";
  const confirmedWidth = showLocationColumn ? "14%" : "15%";
  const stateWidth = showLocationColumn ? "13%" : "15%";
  //the detail panel spans the whole row, so it has to know how many columns there are
  const columnCount = showLocationColumn ? 7 : 6;

  const hoverBgColor = colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];
  const panelBgColor = colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  //keyed on the appointment itself, not its position: an appointment id is unique per
  //location, so an expanded row stays with its own appointment when a filter reorders the
  //table
  const rowKey = (row) => `${row.bid || ""}-${row.appointment_id}`;
  const toggleRow = (key) => setExpandedRows((current) => ({ ...current, [key]: !current[key] }));

  if (isLoading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh" width="100%">
        <Spinner color="blue.500" size="xl" />
      </Flex>
    );
  }

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      {/* Controls */}
      <Flex mb={4} align="center" wrap="wrap" gap={2}>
        <Select
          placeholder="Select range"
          value={selectedRange}
          onChange={(e) => handleDateRangeSelection(e.target.value)}
          width="200px"
          mr={2}
          aria-label="Date range"
        >
          <option value="Today">Today</option>
          <option value="Yesterday">Yesterday</option>
          <option value="Last 7 days">Last 7 days</option>
          <option value="Last 4 weeks">Last 4 weeks</option>
          <option value="Month to Date">Month to Date</option>
          <option value="Quarter to date">Quarter to date</option>
          <option value="Year to date">Year to date</option>
          <option value="Custom">Custom</option>
        </Select>

        <DatePicker
          className={`custom-datepicker ${colorMode === "light" ? "light-theme" : "dark-theme"}`}
          selectsRange
          startDate={startDate}
          endDate={endDate}
          onChange={(update) => {
            setDateRange(update);
            setSelectedRange("Custom");
          }}
          isClearable
          renderCustomHeader={renderCustomHeader}
        />

        <Select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          width="200px"
          ml={2}
          aria-label="Appointment state"
        >
          <option value="All">All appointments</option>
          {STATE_FILTERS.map((state) => (
            <option key={state.value} value={state.value}>{state.label}</option>
          ))}
        </Select>

        <Select
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
          width="200px"
          aria-label="Request channel"
        >
          <option value="All">All channels</option>
          {CHANNEL_FILTERS.map((channel) => (
            <option key={channel.value} value={channel.value}>{channel.label}</option>
          ))}
        </Select>

        <Box ml="auto">
          <Button leftIcon={<FontAwesomeIcon icon={faMagnifyingGlass} />} onClick={() => fetchConfirmations()}>
            Show Confirmations
          </Button>
        </Box>
      </Flex>

      {/* Totals */}
      <Flex mb={2} wrap="wrap" gap={2} data-testid="confirmations-totals">
        <Tag colorScheme="blue"><TagLabel>{`Showing ${visibleRows.length} of ${rows.length} appointments`}</TagLabel></Tag>
        {STATE_FILTERS.map((state) => (
          <Tag key={state.value} colorScheme={STATE_COLORS[state.value]}>
            <TagLabel>{`${state.label}: ${totals.states[state.value]}`}</TagLabel>
          </Tag>
        ))}
        <Tag colorScheme="purple">
          <TagLabel>{`${brand ? `Confirmed by ${brand}` : "Confirmed automatically"}: ${totals.confirmedByViva}`}</TagLabel>
        </Tag>
        <Tag colorScheme="purple"><TagLabel>{`Confirmed by office: ${totals.confirmedByOffice}`}</TagLabel></Tag>
        <Tag colorScheme="gray"><TagLabel>{`Requests sent: ${totals.requests}`}</TagLabel></Tag>
        {CHANNEL_FILTERS.map((channel) => (
          <Tag key={channel.value} colorScheme="teal">
            <TagLabel>{`${channel.label}: ${totals.channels[channel.value]}`}</TagLabel>
          </Tag>
        ))}
      </Flex>

      <Text fontSize="sm" color="gray.500" mb={2}>
        <FontAwesomeIcon icon={faCircleCheck} /> {`Text, Email and Phone call are the channels ${brand ? `${brand} ` : ""}used. `}
        Phone call covers every confirmation taken on a call, and Link / Patient portal covers a
        confirmation made through a link or the patient portal.
      </Text>

      <Flex data-report-download-target justify="flex-end" mb={2} className="no-print" />

      {/* Table — a real <table>, like Reports/Audit.js and Reports/CampaignReport.js.
          The a-dec build carries no report CSV download (Reports/ReportDownloadButton.js is
          a qa-only component, so the marker above stays empty here), but that button scrapes
          the table on screen: given a real table it takes the table's own rows and cells and
          skips a full-width detail row, so the markup stays download-ready — one CSV line per
          visible appointment whatever the row count and whichever rows are expanded. */}
      <Box overflowX="auto" overflowY="auto" maxHeight="80vh">
        <Table
          variant="simple"
          size="sm"
          minWidth="900px"
          sx={{
            tableLayout: "fixed",
            //the column names read as written, as this report has always shown them
            th: { textTransform: "none", letterSpacing: "normal", fontSize: "sm", py: 2 },
            td: { py: 2 },
          }}
        >
          <Thead>
            <Tr>
              <Th width={appointmentWidth}>Appointment</Th>
              {showLocationColumn && <Th width="13%">Location</Th>}
              <Th width={patientWidth}>Patient</Th>
              <Th width={providerWidth}>Provider</Th>
              <Th width={requestsWidth}>Requests sent</Th>
              <Th width={confirmedWidth}>Confirmed</Th>
              <Th width={stateWidth}>Status</Th>
            </Tr>
          </Thead>
          <Tbody fontSize="sm">
            {visibleRows.map((row) => {
              const key = rowKey(row);
              const isExpanded = !!expandedRows[key];
              const sends = row.sends || [];

              return (
                <React.Fragment key={key}>
                  <Tr
                    cursor="pointer"
                    onClick={() => toggleRow(key)}
                    _hover={{ backgroundColor: hoverBgColor }}
                    data-testid="confirmations-row"
                  >
                    <Td>
                      {/* the row is clickable, but the expansion also needs a control a
                          keyboard and a screen reader can reach - the same shape
                          Reports/Audit.js uses for its detail row */}
                      <HStack spacing={2}>
                        <Button
                          size="xs"
                          variant="ghost"
                          px={1}
                          minW="auto"
                          aria-expanded={isExpanded}
                          aria-label={`${isExpanded ? "Hide" : "Show"} what was sent for the appointment on ${moment(row.starttime).format("MMM D, YYYY h:mm a")}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleRow(key);
                          }}
                        >
                          <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                        </Button>
                        <Text>{moment(row.starttime).format("MMM D, YYYY h:mm a")}</Text>
                      </HStack>
                    </Td>
                    {showLocationColumn && <Td>{row.location_name || "-"}</Td>}
                    <Td>{row.patient_name || "-"}</Td>
                    <Td>{row.provider_name || "-"}</Td>
                    <Td>{requestsText(row)}</Td>
                    <Td>
                      <Tag
                        size="md"
                        variant="subtle"
                        colorScheme={row.confirmed_by === "viva" ? "green" : (row.confirmed_by === "office" ? "blue" : "yellow")}
                      >
                        {/* the channel AND the time it was confirmed have to be readable in the
                            column, and a Tag label is clamped to one line by default */}
                        <TagLabel whiteSpace="normal" noOfLines={2}>{confirmationText(row)}</TagLabel>
                      </Tag>
                    </Td>
                    <Td>{stateText(row)}</Td>
                  </Tr>

                  {isExpanded && (
                    <Tr>
                      <Td colSpan={columnCount} p={0}>
                        <Box p={3} pl={6} backgroundColor={panelBgColor} data-testid="confirmations-detail">
                          <Text fontWeight="bold" mb={1}>
                            {`Confirmation requests (${sends.length})`}
                          </Text>
                          {sends.length === 0 && (
                            <Text fontSize="sm" color="gray.500">
                              {brand
                                ? `${brand} sent no confirmation request for this appointment.`
                                : "No confirmation request was sent for this appointment."}
                            </Text>
                          )}
                          <VStack spacing={1} align="stretch">
                            {sends.map((send, sendIndex) => (
                              <HStack key={`${send.source_id}-${send.channel}-${sendIndex}`} spacing={6} fontSize="sm">
                                <Text flexBasis="22%">{moment(send.sent_at).format("MMM D, YYYY h:mm a")}</Text>
                                <Text flexBasis="16%">{send.channel_label || "Unknown channel"}</Text>
                                <Text flexBasis="34%">{sendOutcomeText(send)}</Text>
                                <Text flexBasis="28%">
                                  {[send.campaign_name, send.purpose].filter(Boolean).join(" • ") || "-"}
                                </Text>
                              </HStack>
                            ))}
                          </VStack>
                          <Text fontWeight="bold" mt={3} mb={1}>Confirmation</Text>
                          <Text fontSize="sm">
                            {row.confirmed_by === "viva"
                              ? `${confirmationText(row)} (recorded as "${row.confirmation_method}")`
                              : confirmationText(row)}
                          </Text>
                          {(row.appointment_state === "cancelled" || row.appointment_state === "deleted") && (
                            <Text fontSize="sm" mt={1}>
                              {stateText(row)}
                              {row.state_changed_at_is_approximate
                                ? " — the time the change was picked up from the practice management system"
                                : ""}
                            </Text>
                          )}
                        </Box>
                      </Td>
                    </Tr>
                  )}
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

export default ConfirmationsReport;
